import { useMemo } from 'react';
import type { Node } from '@xyflow/react';
import type { imageModels } from '@flowmuse/models/image';
import { getEffectiveImageConfig } from '@flowmuse/models/image';
import { type InlineMediaConfig, classifyNodeMediaType } from '@flowmuse/models/inline-attachments';
import { nodeHasImageContent, nodeHasTextContent } from '@/components/nodes/shared/media-content';

export interface MediaProgress {
  current: number;
  max: number;
}

export interface ImageGuidanceData {
  needsImage: boolean;
  imageProgress?: MediaProgress;
  currentMode: string;
  shouldShowCurrentMode: boolean;
}

interface UseImageGuidanceParams {
  selectedModel: (typeof imageModels)[string] | null | undefined;
  data: any;
  id: string;
  incomers: Node[];
  /** 当前是否处于图生图模式；文生图模式不提示连图/不显示计数 */
  isI2IMode?: boolean;
  t: (key: string) => string;
}

// 节点是否为已连接（含未上传）的图像节点
function isConnectedImageNode(n: any): boolean {
  if (n.type === 'image') return true;
  if (n.type === 'upload') {
    const fileType = n.data?.fileType;
    const contentType = n.data?.content?.type;
    if (fileType === 'image' || (contentType && contentType.startsWith('image/'))) return true;
    // 新添加但尚无 fileType/content 的上传节点也视为图像节点
    if (!fileType && !contentType) return true;
  }
  return false;
}

export function useImageGuidance({
  selectedModel,
  data,
  incomers,
  isI2IMode = true,
  t,
}: UseImageGuidanceParams): ImageGuidanceData {
  // 仅依赖实际用到的字段，避免 data 引用变化导致的无谓重算，
  // 同时确保 inlineAttachments 变化时计数能正确更新。
  const instructions = data?.instructions as string | undefined;
  const inlineAttachments = data?.inlineAttachments as unknown[] | undefined;
  const imageMode = data?.imageMode as string | undefined;

  return useMemo(() => {
    if (!selectedModel) {
      return { needsImage: false, currentMode: t('Text to Image'), shouldShowCurrentMode: false };
    }

    // 单次遍历 incomers，同时统计图像计数与内容标志。
    let hasIncomingImageNodes = false;
    let hasIncomingTextNodes = false;
    let hasConnectedImageNodes = false;
    let connectedImageNodesCount = 0;
    for (const n of incomers) {
      if (classifyNodeMediaType(n as any) === 'image') connectedImageNodesCount++;
      if (!hasIncomingImageNodes && nodeHasImageContent(n)) hasIncomingImageNodes = true;
      if (!hasIncomingTextNodes && nodeHasTextContent(n)) hasIncomingTextNodes = true;
      if (!hasConnectedImageNodes && isConnectedImageNode(n)) hasConnectedImageNodes = true;
    }

    const supportsInput = selectedModel.supportsInput || [];
    // 文生图模式下即便模型支持 i2i 也不应提示连图/显示计数
    const supportsI2I = isI2IMode && (supportsInput.includes('i2i') || selectedModel.supportsEdit);

    // 计数：已连接图片节点 + 内联图片附件，以及模型允许的最大图片数
    const inlineAtts = Array.isArray(inlineAttachments) ? inlineAttachments as any[] : [];
    const imCfg: InlineMediaConfig | undefined = getEffectiveImageConfig(selectedModel as any, imageMode).inlineMedia;
    let inlineImageCount = 0;
    for (const a of inlineAtts) {
      if (a?.fileType === 'image' && a?.url) inlineImageCount++;
    }
    const totalImages = connectedImageNodesCount + inlineImageCount;
    const imgMax = imCfg?.image?.maxCount ?? 1;
    const imageProgress: MediaProgress | undefined =
      supportsI2I && imgMax > 1 ? { current: totalImages, max: imgMax } : undefined;

    const needsImage =
      supportsI2I &&
      (imgMax > 1 ? totalImages < imgMax : !hasIncomingImageNodes && !hasConnectedImageNodes);

    // 当前模式：仅当既有图像输入又有提示词输入时为图生图，否则文生图
    const hasPromptInput = hasIncomingTextNodes || Boolean(instructions?.trim());
    const currentMode = hasIncomingImageNodes && hasPromptInput ? t('Image to Image') : t('Text to Image');

    return {
      needsImage,
      imageProgress,
      currentMode,
      shouldShowCurrentMode: hasPromptInput,
    };
  }, [selectedModel, instructions, inlineAttachments, imageMode, incomers, isI2IMode, t]);
}
